import { useForm, router } from '@inertiajs/react'
import { type FormEvent, useState, useRef, useCallback } from 'react'

interface BorrowerLoan {
  id: number
  lender_name: string
  loan_amount: number
  interest_rate: number
  term_months: number
  loan_type: string
  origination_date: string
  maturity_date: string
  origination_points: number
  monthly_payment: number
  status: string
  notes: string | null
  property_address: string | null
  property_type: string | null
  days_to_maturity: number
  maturity_urgency: string
}

interface Props {
  loan?: BorrowerLoan | null
  onClose: () => void
  isPro?: boolean
}

interface ParsedFields {
  lender_name?: string | null
  loan_amount?: number | null
  interest_rate?: number | null
  term_months?: number | null
  loan_type?: string | null
  origination_date?: string | null
  maturity_date?: string | null
  origination_points?: number | null
  monthly_payment?: number | null
  notes?: string | null
}

const LOAN_TYPES = [
  { value: 'bridge', label: 'Bridge' },
  { value: 'dscr', label: 'DSCR' },
  { value: 'interest_only', label: 'Interest Only' },
  { value: 'amortizing', label: 'Amortizing' },
  { value: 'other', label: 'Other' },
]

const PROPERTY_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'land', label: 'Land' },
  { value: 'mixed_use', label: 'Mixed Use' },
  { value: 'other', label: 'Other' },
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paid_off', label: 'Paid Off' },
  { value: 'defaulted', label: 'Defaulted' },
]

const inputClass =
  'w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

export default function LoanForm({ loan, onClose, isPro = false }: Props) {
  const isEditing = !!loan
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [confidence, setConfidence] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [parsedFieldKeys, setParsedFieldKeys] = useState<Set<string>>(new Set())
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const { data, setData, post, patch, processing, errors } = useForm({
    lender_name: loan?.lender_name ?? '',
    loan_amount: loan?.loan_amount?.toString() ?? '',
    interest_rate: loan?.interest_rate?.toString() ?? '',
    term_months: loan?.term_months?.toString() ?? '',
    loan_type: loan?.loan_type ?? 'bridge',
    origination_date: loan?.origination_date ?? '',
    maturity_date: loan?.maturity_date ?? '',
    origination_points: loan?.origination_points?.toString() ?? '0',
    monthly_payment: loan?.monthly_payment?.toString() ?? '',
    status: loan?.status ?? 'active',
    notes: loan?.notes ?? '',
    property_address: loan?.property_address ?? '',
    property_type: loan?.property_type ?? '',
  })

  const applyParsedFields = useCallback((fields: ParsedFields) => {
    const filled = new Set<string>()
    if (fields.lender_name) { setData('lender_name', fields.lender_name); filled.add('lender_name') }
    if (fields.loan_amount != null) { setData('loan_amount', fields.loan_amount.toString()); filled.add('loan_amount') }
    if (fields.interest_rate != null) { setData('interest_rate', fields.interest_rate.toString()); filled.add('interest_rate') }
    if (fields.term_months != null) { setData('term_months', fields.term_months.toString()); filled.add('term_months') }
    if (fields.loan_type) { setData('loan_type', fields.loan_type); filled.add('loan_type') }
    if (fields.origination_date) { setData('origination_date', fields.origination_date); filled.add('origination_date') }
    if (fields.maturity_date) { setData('maturity_date', fields.maturity_date); filled.add('maturity_date') }
    if (fields.origination_points != null) { setData('origination_points', fields.origination_points.toString()); filled.add('origination_points') }
    if (fields.monthly_payment != null) { setData('monthly_payment', fields.monthly_payment.toString()); filled.add('monthly_payment') }
    if (fields.notes) { setData('notes', fields.notes); filled.add('notes') }
    setParsedFieldKeys(filled)
  }, [setData])

  async function handleFileUpload(file: File) {
    if (file.type !== 'application/pdf') {
      setParseError('Only PDF files are supported.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setParseError('File is too large (max 10 MB).')
      return
    }

    setParsing(true)
    setParseError(null)
    setConfidence(null)
    setParsedFieldKeys(new Set())

    const formData = new FormData()
    formData.append('document', file)

    try {
      const response = await fetch('/my-loans/loans/parse', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken() },
        body: formData,
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        setParseError(result.error || 'Failed to parse document.')
        return
      }

      if (result.parsed_fields && Object.keys(result.parsed_fields).length > 0) {
        applyParsedFields(result.parsed_fields)
        setConfidence(result.confidence)
        setUploadedFile(file)
      } else {
        setParseError('No loan details could be extracted. Please enter them manually.')
      }
    } catch {
      setParseError('Network error. Please try again or enter details manually.')
    } finally {
      setParsing(false)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileUpload(file)
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFileUpload(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (isEditing && loan) {
      patch(`/my-loans/loans/${loan.id}`, { onSuccess: onClose })
    } else if (uploadedFile) {
      // Include the PDF attachment alongside loan fields via Inertia router
      // Inertia auto-converts to FormData when a File is present in the data
      router.post('/my-loans/loans', {
        borrower_loan: data,
        document: uploadedFile,
      }, { onSuccess: onClose })
    } else {
      post('/my-loans/loans', { onSuccess: onClose })
    }
  }

  const confidenceStyles = {
    high: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'High confidence' },
    medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Medium confidence — please verify' },
    low: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Low confidence — review carefully' },
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative min-h-full flex items-start justify-center py-8 px-4">
        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Loan' : 'Add a Loan'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* PDF Upload Zone — only for new loans */}
          {!isEditing && (
            <div className="px-6 pt-4">
              {isPro ? (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`relative rounded-xl border-2 border-dashed p-4 text-center transition-colors ${
                    dragOver ? 'border-emerald-400 bg-emerald-50' :
                    parsing ? 'border-gray-300 bg-gray-50' :
                    'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50'
                  }`}
                >
                  {parsing ? (
                    <div className="flex items-center justify-center gap-2 py-2">
                      <svg className="w-5 h-5 text-emerald-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-sm text-gray-600">Parsing loan document...</span>
                    </div>
                  ) : (
                    <>
                      <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                      <p className="text-sm text-gray-600">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="font-semibold text-emerald-600 hover:text-emerald-700"
                        >
                          Upload a loan document
                        </button>
                        {' '}or drag and drop
                      </p>
                      <p className="text-xs text-gray-400 mt-1">PDF up to 10 MB — promissory notes, term sheets, loan agreements</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileInput}
                        className="hidden"
                      />
                    </>
                  )}
                </div>
              ) : (
                <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-500">AI Document Parser</span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Pro</span>
                  </div>
                  <p className="text-xs text-gray-400">Upload a loan document and auto-fill your loan details</p>
                </div>
              )}

              {/* Parse error */}
              {parseError && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                  <p className="text-xs text-red-600">{parseError}</p>
                </div>
              )}

              {/* Confidence indicator */}
              {confidence && !parseError && (
                <div className={`mt-2 rounded-lg border px-3 py-2 ${confidenceStyles[confidence as keyof typeof confidenceStyles]?.bg} ${confidenceStyles[confidence as keyof typeof confidenceStyles]?.border}`}>
                  <p className={`text-xs font-medium ${confidenceStyles[confidence as keyof typeof confidenceStyles]?.text}`}>
                    {confidenceStyles[confidence as keyof typeof confidenceStyles]?.label} — review the fields below and correct anything that looks wrong.
                  </p>
                </div>
              )}

              <div className="mt-3 mb-1 flex items-center gap-2">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400 uppercase tracking-wide">
                  {confidence ? 'Review & confirm' : 'Or enter manually'}
                </span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <Field label="Lender Name" error={errors.lender_name} highlighted={parsedFieldKeys.has('lender_name')}>
              <input
                type="text"
                value={data.lender_name}
                onChange={(e) => setData('lender_name', e.target.value)}
                placeholder="e.g. ABC Capital"
                required
                className={inputClass}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Loan Amount ($)" error={errors.loan_amount} highlighted={parsedFieldKeys.has('loan_amount')}>
                <input
                  type="number"
                  value={data.loan_amount}
                  onChange={(e) => setData('loan_amount', e.target.value)}
                  placeholder="250000"
                  step="0.01"
                  min="0"
                  required
                  className={inputClass}
                />
              </Field>
              <Field label="Interest Rate (%)" error={errors.interest_rate} highlighted={parsedFieldKeys.has('interest_rate')}>
                <input
                  type="number"
                  value={data.interest_rate}
                  onChange={(e) => setData('interest_rate', e.target.value)}
                  placeholder="12.0"
                  step="0.001"
                  min="0"
                  max="100"
                  required
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Term (months)" error={errors.term_months} highlighted={parsedFieldKeys.has('term_months')}>
                <input
                  type="number"
                  value={data.term_months}
                  onChange={(e) => setData('term_months', e.target.value)}
                  placeholder="12"
                  min="1"
                  required
                  className={inputClass}
                />
              </Field>
              <Field label="Loan Type" error={errors.loan_type} highlighted={parsedFieldKeys.has('loan_type')}>
                <select
                  value={data.loan_type}
                  onChange={(e) => setData('loan_type', e.target.value)}
                  className={inputClass}
                >
                  {LOAN_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Origination Date" error={errors.origination_date} highlighted={parsedFieldKeys.has('origination_date')}>
                <input
                  type="date"
                  value={data.origination_date}
                  onChange={(e) => setData('origination_date', e.target.value)}
                  required
                  className={inputClass}
                />
              </Field>
              <Field label="Maturity Date" error={errors.maturity_date} highlighted={parsedFieldKeys.has('maturity_date')}>
                <input
                  type="date"
                  value={data.maturity_date}
                  onChange={(e) => setData('maturity_date', e.target.value)}
                  required
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Origination Points (%)" error={errors.origination_points} highlighted={parsedFieldKeys.has('origination_points')}>
                <input
                  type="number"
                  value={data.origination_points}
                  onChange={(e) => setData('origination_points', e.target.value)}
                  placeholder="2.0"
                  step="0.01"
                  min="0"
                  className={inputClass}
                />
              </Field>
              <Field label="Monthly Payment ($)" error={errors.monthly_payment} highlighted={parsedFieldKeys.has('monthly_payment')}>
                <input
                  type="number"
                  value={data.monthly_payment}
                  onChange={(e) => setData('monthly_payment', e.target.value)}
                  placeholder="2500"
                  step="0.01"
                  min="0"
                  required
                  className={inputClass}
                />
              </Field>
            </div>

            {isEditing && (
              <Field label="Status" error={errors.status}>
                <select
                  value={data.status}
                  onChange={(e) => setData('status', e.target.value)}
                  className={inputClass}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Property Address (optional)" error={errors.property_address}>
              <input
                type="text"
                value={data.property_address}
                onChange={(e) => setData('property_address', e.target.value)}
                placeholder="e.g. 123 Main St, Austin, TX"
                className={inputClass}
              />
            </Field>

            <Field label="Property Type (optional)" error={errors.property_type}>
              <select
                value={data.property_type}
                onChange={(e) => setData('property_type', e.target.value)}
                className={inputClass}
              >
                {PROPERTY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Notes (optional)" error={errors.notes}>
              <textarea
                value={data.notes}
                onChange={(e) => setData('notes', e.target.value)}
                placeholder="Any additional details about this loan..."
                rows={3}
                className={inputClass}
              />
            </Field>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="loan-form"
              disabled={processing}
              onClick={(e) => {
                e.preventDefault()
                const form = document.querySelector<HTMLFormElement>('form')
                form?.requestSubmit()
              }}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {processing ? 'Saving...' : isEditing ? 'Save Changes' : confidence ? 'Confirm & Save' : 'Add Loan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({
  label,
  error,
  children,
  highlighted = false,
}: {
  label: string
  error?: string
  children: React.ReactNode
  highlighted?: boolean
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {highlighted && (
          <span className="ml-1.5 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full uppercase">
            Parsed
          </span>
        )}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  )
}
